import { useRef, useState } from "react";
import { IReportMetricsRow } from "../../../../@types/reports-metrics.type";
import { SelectedType } from "./../../index";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { themeGrid } from "../../../../themeGrid";
import { localeText } from "../../../../localeTextFromGrid";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import {
  ColDef,
} from "ag-grid-community";
import styles from "./styles.module.scss";
import { GridToPrint } from "../GridToPrint";
import { handlePrintFilteredRows } from "../../utils/handlePrintFilteredRows";
import iconExcel from "./../../../../assets/icon-excel.svg";
import { downloadResourcesReportToExcel } from "../../../../services/api/resource/resourceService";
import { downloadProjectsReportToExcel } from "../../../../services/api/projects/projectService";
import { downloadCustomersReportToExcel } from "../../../../services/api/customers/CustomersService";
import { downloadPositionsReportToExcel } from "../../../../services/api/positions/PositionsService";
import { downloadAreasReportToExcel } from "../../../../services/api/areas/AreasService";
import { downloadCostCentersReportToExcel } from "../../../../services/api/costCenter/costCenterService";
import { Button } from "../../../../components/Button";
import { calculateTimeRange } from "../../../../utils/timeUtils";


export interface RowDataDefaultGrid {
  id?: string | undefined;
  name: string;
  totalScheduled: string;
  percentScheduled?: number;
  billed: string;
  notBilled: string;
}

interface DefaultDataProps {
  columnName?: string;
  rows: RowDataDefaultGrid[];
  rowCount: number;
  selectedType: SelectedType | null;
  setSelectedType: React.Dispatch<React.SetStateAction<SelectedType | null>>;
  setRowSelected: React.Dispatch<
    React.SetStateAction<IReportMetricsRow | null>
  >;
  setValueTab: React.Dispatch<React.SetStateAction<number>>;
  valueTab: number;
  loading: boolean;
  filterState: any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const parseValue = (value: string): number =>
  parseFloat(value.replace(/[hd]/, "")) || 0;

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3, padding: 0 }}>{children}</Box>}
    </div>
  );
}

export const DefaultDataGridReport: React.FC<DefaultDataProps> = ({
  columnName = "",
  rows = [],
  rowCount = 0,
  selectedType,
  setSelectedType,
  setRowSelected,
  setValueTab,
  valueTab,
  loading,
  filterState
}) => {
  const [value, setValue] = useState(0);
  const [filteredRowsForPrint, setFilteredRowsForPrint] = useState<
    RowDataDefaultGrid[]
  >([]);
  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const gridRef = useRef<AgGridReact<RowDataDefaultGrid>>(null);

  const columns: ColDef[] = [
    {
      field: "name",
      headerName: columnName,
      flex: 2,
      minWidth: 100,
    },
    {
      field: "totalScheduled",
      headerName: "Total Agendadas",
      flex: 1,
      minWidth: 70,
      comparator: (v1: string, v2: string) => parseValue(v1) - parseValue(v2),
      cellRenderer: (params: CustomCellRendererProps) => {
        const total = params.data.totalScheduled;
        if (columnName === "Função") {
          const percent = params.data.percentScheduled;
          const displayPercent = Math.min(percent, 100);
          const progressBarColor = percent > 100 ? "#F45B5B" : "";
          return (
            <div className={styles.container}>
              <div className={styles.progressBarContainer}>
                <div
                  className={styles.progressBar}
                  style={{
                    width: `${displayPercent}%`,
                    backgroundColor: progressBarColor,
                  }}
                  title={`${percent}%`}
                />
              </div>
              <span className={styles.totalText}>{total}</span>
            </div>
          );
        } else {
          return <span className={styles.totalText}>{total}</span>;
        }
      },
    },
    {
      field: "billed",
      headerName: "Faturável",
      minWidth: 70,
      flex: 1,
      comparator: (v1: string, v2: string) => parseValue(v1) - parseValue(v2),
    },
    {
      field: "notBilled",
      headerName: "Não Faturável",
      flex: 1,
      minWidth: 70,
      comparator: (v1: string, v2: string) => parseValue(v1) - parseValue(v2),
    },
  ];

  const newColumn: ColDef = {
    field: "customer",
    headerName: "Cliente",
    flex: 1,
    minWidth: 100,
  };
  const newColumnUnavailability: ColDef = {
    field: "unavailability",
    headerName: "Indisponivel",
    flex: 1.5,
    minWidth: 70,
  };

  const newColumnProjection: ColDef = {
    field: "projection",
    headerName: "Projetadas",
    flex: 1.5,
    minWidth: 70,
  };

  const newColumnContracted: ColDef = {
    field: "contractedHours",
    headerName: "Contratado",
    flex: 1,
    minWidth: 70,
    comparator: (v1: string, v2: string) => parseValue(v1) - parseValue(v2),
  };

  const updatedColumns =
    columnName === "Projeto"
      ? [...columns.slice(0, 1), newColumn, newColumnContracted, ...columns.slice(1), newColumnProjection]
      : columnName === "Cliente" ||
        columnName === "Função" ||
        columnName === "Área" ||
        columnName === "Centro de Custo"
      ? [...columns.slice(0, 1), newColumnContracted, ...columns.slice(1)]
      : columnName == "Colaboradores" && selectedType != "project"
      ? [...columns, newColumnUnavailability]
      : columns;

  function handleFilteredForPrint() {
    handlePrintFilteredRows({ gridRef, setFilteredRowsForPrint, rows });
  }

  async function ExportToExcel() {
    const { startDate, endDate } = calculateTimeRange(filterState?.currentPeriod, filterState?.period);
    const filter = { startTime: startDate.startOf('day').toISOString(), endTime: endDate.startOf('day').toISOString(), timeType: 1} as IReportMetricsBody;

    switch(valueTab){
      case 0:
        downloadResourcesReportToExcel(filter);
        break;
      case 1:
        downloadProjectsReportToExcel(filter);
        break;
      case 2:
        downloadCustomersReportToExcel(filter);
        break;
      case 3:
        downloadPositionsReportToExcel(filter);
        break;
      case 4:
        downloadAreasReportToExcel(filter);
        break;;
      case 5:
        downloadCostCentersReportToExcel(filter);
        break;
      default: 
        return "";
    }
  }

  return (
    <>
      <main className={styles.dataGridStyle}>
        <Box className={styles.customBox}>
          <Box className={styles.borderedBox}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="basic tabs example"
              classes={{
                flexContainer: styles.customFlexContainer,
                indicator: styles["MuiTabs-indicator"],
              }}
            >
              <Tab label="Tabela de Alocação" className={styles.tab} />
              <Box sx={{marginLeft: "auto", display: "flex"}}>
                <Button
                  onClick={() => {
                    ExportToExcel();
                  }}
                  iconUrl={iconExcel}
                  className={styles.btnExcel}
                />
              </Box>
            </Tabs>
          </Box>
        </Box>
        <CustomTabPanel value={value} index={0}>
          <div className={`${styles.grid} no-print`}>
            <AgGridReact
              ref={gridRef}
              rowData={rows}
              columnDefs={updatedColumns}
              theme={themeGrid}
              localeText={localeText}
              loading={loading}
              overlayNoRowsTemplate="<span style='color: gray;'>Sem registros encontrados</span>"
              defaultColDef={{
                sortable: true,
                editable: false,
                filter: true,
                floatingFilter: true,
              }}
              suppressCellFocus={true}
              rowStyle={{ cursor: "pointer" }}
              onRowClicked={(row) => {
                setRowSelected(row.data);
                if (valueTab == 1) {
                  setValueTab(0);
                  setSelectedType("project");
                } else if (valueTab == 3) {
                  setValueTab(0);
                } else {
                  setValueTab(1);
                  if (selectedType == "position" || selectedType == "project") {
                    setSelectedType("resource");
                  }
                }
              }}
              animateRows={true}
              onGridReady={handleFilteredForPrint}
              onFilterChanged={handleFilteredForPrint}
            />
          </div>

          <GridToPrint
            columns={updatedColumns}
            rows={filteredRowsForPrint.length > 0 ? filteredRowsForPrint : rows}
            typeGrid="default"
            columnName={columnName}
          />
        </CustomTabPanel>
      </main>
    </>
  );
};
